<template>
  <div class="step-panel">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Adiantamentos de Colaboradores') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Adiantamentos Independentes') }}</strong>
        <ul>
          <li>{{ t('Selecione adiantamentos abertos ou parciais para descontar neste período') }}</li>
          <li>{{ t('Você pode descontar valores parciais de cada adiantamento') }}</li>
          <li>{{ t('O status será atualizado automaticamente (aberto → parcial → pago)') }}</li>
        </ul>
      </div>
    </div>

    <!-- Loading State from Machine -->
    <div v-if="isLoading" class="loading-container">
      <spinner />
    </div>

    <!-- Error State from Machine -->
    <div v-else-if="hasError" class="error-container">
      <inline-message severity="error" class="w-full">
        {{ t('Erro ao carregar adiantamentos. Clique em tentar novamente.') }}
      </inline-message>
      <Button
        :label="t('Tentar Novamente')"
        icon="pi pi-refresh"
        data-testid="payroll-advances-retry-btn"
        @click="handleRetry"
        class="mt-3"
      />
    </div>

    <!-- Interactive State -->
    <div v-else class="advances-container">
      <!-- Cash Recebimentos Info Banner -->
      <div v-if="availableCashCount > 0" class="cash-recebimentos-card">
        <div class="card-header-section">
          <div class="header-left">
            <i class="pi pi-money-bill text-green-500"></i>
            <h4>{{ t('Recebimentos em Dinheiro Disponíveis') }}</h4>
          </div>
        </div>
        <div class="card-content">
          <div class="cash-summary">
            <span class="text-600">
              {{ t('Found') }} <strong>{{ availableCashCount }}</strong> {{ t('cash payments available for conversion to advances') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Employees with Advances Summary Card -->
      <div class="advances-summary-card">
        <PayrollConciliationStatusCard
          :status="conciliationStatus"
          :summary-items="summaryItems"
          :show-action="true"
          :action-label="t('Gerenciar Adiantamentos')"
          :button-icon="advancesSelected > 0 ? 'pi pi-pencil' : 'pi pi-plus-circle'"
          :button-severity="advancesSelected > 0 ? 'warning' : 'success'"
          :status-message="statusMessage"
          @action="openAdvancesWizard"
        />
      </div>

      <!-- EmployeeAdvancesConciliationWizard -->
      <EmployeeAdvancesConciliationWizard
        v-if="periodId && franchiseId"
        :visible="showAdvancesWizard"
        :period-id="periodId"
        :franchise-id="franchiseId"
        :users-map="usersMap"
        :initial-selections="selectedDiscounts"
        @update:visible="showAdvancesWizard = $event"
        @update:selections="handleSelectionsUpdate"
        @completed="handleWizardCompleted"
        @closed="handleWizardClosed"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { eventBus } from '@/eventBus';
import PayrollConciliationStatusCard from './shared/PayrollConciliationStatusCard.vue';
import EmployeeAdvancesConciliationWizard from './EmployeeAdvancesConciliationWizard.vue';

type Id = number | string;
type ConciliationStatus = 'pending' | 'in_progress' | 'completed';

interface AdvanceDiscount {
  advance_id: Id;
  amount: number | string;
}

interface PaymentUser {
  user_id: Id;
  [key: string]: unknown;
}

interface MachineContext {
  period?: { id?: Id; franchise_id?: Id };
  periodFormData?: { franchise_id?: Id };
  error?: { message?: string };
  data?: {
    advances?: {
      available?: unknown[];
      summary_by_employee?: unknown[];
      total?: number;
      cash_recebimentos?: unknown[];
    };
  };
  advanceDiscounts?: AdvanceDiscount[];
  paymentByUser?: PaymentUser[];
}

interface WizardMachine {
  context: { value: MachineContext };
  state: { value: { matches: (s: string) => boolean } };
  send: (event: { type: string; [key: string]: unknown }) => void;
}

// =============================================================================
// SINGLE SOURCE OF TRUTH: XState Machine
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;
const { t, n } = useI18n();

// =============================================================================
// MACHINE STATE READ-ONLY ACCESS
// =============================================================================

const periodId = computed((): number | null => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

const franchiseId = computed((): number | null => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

const isLoading = computed((): boolean =>
  machine.state.value.matches('employeeAdvances.loading')
);

const hasError = computed((): boolean =>
  machine.state.value.matches('employeeAdvances.error')
);

const advancesData = computed(() => {
  const data = machine.context.value.data?.advances;
  return {
    advances: data?.available ?? [],
    summary_by_employee: data?.summary_by_employee ?? [],
    total_open: data?.total ?? 0,
    available_cash_recebimentos: data?.cash_recebimentos ?? [],
  };
});

/**
 * Selected discounts from machine context (legacy: advanceDiscounts)
 */
const selectedDiscounts = computed((): Record<string, number> => {
  const discounts = machine.context.value.advanceDiscounts ?? [];
  const selected: Record<string, number> = {};
  discounts.forEach((item: AdvanceDiscount) => {
    selected[String(item.advance_id)] = parseFloat(String(item.amount ?? 0)) || 0;
  });
  return selected;
});

const usersMap = computed((): Record<string, PaymentUser> => {
  const users = machine.context.value.paymentByUser ?? [];
  const map: Record<string, PaymentUser> = {};
  users.forEach((u: PaymentUser) => {
    map[String(u.user_id)] = u;
  });
  return map;
});

// =============================================================================
// COMPUTED PROPERTIES (derived from machine state only)
// =============================================================================

const selectedCount = computed((): number =>
  Object.keys(selectedDiscounts.value).length
);

const advancesSelected = computed((): number =>
  Object.values(selectedDiscounts.value).filter((amount: number) => amount > 0).length
);

const totalToDiscount = computed((): number =>
  Object.values(selectedDiscounts.value).reduce((sum: number, amount: number) => sum + amount, 0)
);

const availableCashCount = computed((): number =>
  advancesData.value.available_cash_recebimentos?.length ?? 0
);

const conciliationStatus = computed((): ConciliationStatus => {
  if (selectedCount.value === 0) return 'pending';
  return 'in_progress';
});

const summaryItems = computed(() => [
  {
    label: t('Adiantamentos Selecionados'),
    value: advancesSelected.value.toString(),
    icon: 'pi pi-check-square',
  },
  {
    label: t('Valor a Descontar'),
    value: n(totalToDiscount.value, 'currency'),
    highlight: true,
    icon: 'pi pi-money-bill',
  },
]);

const statusMessage = computed((): string => {
  if (advancesSelected.value === 0) {
    return t('Clique no botão para gerenciar adiantamentos e recebimentos em dinheiro.');
  }
  return t('{count} adiantamentos selecionados no valor de {total}.', {
    count: advancesSelected.value,
    total: n(totalToDiscount.value, 'currency'),
  });
});

// =============================================================================
// UI STATE (component-scoped only)
// =============================================================================

const showAdvancesWizard = ref<boolean>(false);

// =============================================================================
// EVENT HANDLERS (send events to machine)
// =============================================================================

const openAdvancesWizard = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeAdvances', 'OPEN_WIZARD', {
    selectedDiscountsKeys: Object.keys(selectedDiscounts.value),
    values: Object.values(selectedDiscounts.value),
    total: totalToDiscount.value,
  });
  showAdvancesWizard.value = true;
};

const handleSelectionsUpdate = (newSelections: Record<string, number | string>): void => {
  const normalized: Record<string, number> = {};
  for (const [key, value] of Object.entries(newSelections)) {
    normalized[key] = parseFloat(String(value ?? 0)) || 0;
  }
  const newTotal = Object.values(normalized).reduce(
    (sum: number, amount: number) => sum + amount,
    0
  );
  log('PAYROLL_REFACTOR', 'DATA_FLOW', 'COMPONENT', 'WRITE', {
    via: 'machine.send()',
    event: 'UPDATE_ADVANCE_SELECTIONS',
    selectionCount: Object.keys(normalized).length,
    total: newTotal,
  });
  machine.send({ type: 'UPDATE_ADVANCE_SELECTIONS', selections: normalized });
};

const handleWizardCompleted = (result: unknown): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeAdvances', 'WIZARD_COMPLETED', result);
  eventBus.emit('globalMessage.success', t('Adiantamentos processados com sucesso'));
  showAdvancesWizard.value = false;
  machine.send({ type: 'RELOAD_ADVANCES' });
};

const handleWizardClosed = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeAdvances', 'WIZARD_CLOSED');
  showAdvancesWizard.value = false;
};

const handleRetry = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeAdvances', 'RETRY_LOAD');
  machine.send({ type: 'RETRY_ADVANCES' });
};

// =============================================================================
// LIFECYCLE & WATCH
// =============================================================================

watch(periodId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepEmployeeAdvances', 'PERIOD_CHANGED', {
      oldPeriodId: oldId,
      newPeriodId: newId,
      action: 'Machine will auto-reload advances data',
    });
    // Machine handles reload via state transitions
  }
}, { immediate: false });
</script>

<style scoped>
.step-panel {
  height: 100%;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.helper-banner {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 3px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.advances-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.cash-recebimentos-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.card-header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: var(--green-50);
  border-bottom: 1px solid var(--surface-border);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-left i {
  font-size: 1.25rem;
}

.header-left h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.card-content {
  padding: 1rem 1.5rem;
}

.cash-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.advances-summary-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  padding: 1rem;
}

.w-full {
  width: 100%;
}

.mt-3 {
  margin-top: 0.75rem;
}
</style>
